import { useState, useEffect } from 'react';
import { Wallet as WalletIcon, TrendingUp, TrendingDown, RefreshCw, Loader, Plus, Upload, X } from 'lucide-react';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { formatDate, formatDateTime, formatTime } from '../utils/dateUtils';

const Wallet = () => {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [topupRequests, setTopupRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [activeTab, setActiveTab] = useState('transactions'); // 'transactions' or 'topup'
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionTotal, setTransactionTotal] = useState(0);
  const [topupPage, setTopupPage] = useState(1);
  const [topupTotal, setTopupTotal] = useState(0);
  const [topupForm, setTopupForm] = useState({
    amount: '',
    transfer_type: 'NEFT',
    reference_number: '',
    payment_proof: null
  });
  const [submittingTopup, setSubmittingTopup] = useState(false);

  useEffect(() => {
    loadWalletData();
  }, [transactionPage, topupPage]);

  const loadWalletData = async () => {
    try {
      setLoading(true);

      // Load wallet balance
      const walletResponse = await apiService.getWalletBalance();
      if (walletResponse.success) {
        setWallet(walletResponse.data);
      }

      // Load wallet transactions with pagination
      const transactionsResponse = await apiService.getWalletTransactions({
        page: transactionPage,
        limit: 10
      });
      if (transactionsResponse.success) {
        setTransactions(transactionsResponse.data || []);
        setTransactionTotal(transactionsResponse.meta?.pagination?.totalCount || 0);
      }

      // Load top-up requests with pagination
      const topupResponse = await apiService.getTopupRequests({
        page: topupPage,
        limit: 10
      });
      if (topupResponse.success) {
        setTopupRequests(topupResponse.data || []);
        setTopupTotal(topupResponse.meta?.pagination?.totalCount || 0);
      }

    } catch (error) {
      console.error('Failed to load wallet data:', error);
      toast.error('Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  };

  const handleTopupSubmit = async (e) => {
    e.preventDefault();

    if (!topupForm.amount || !topupForm.reference_number || !topupForm.payment_proof) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      setSubmittingTopup(true);

      const formData = new FormData();
      formData.append('amount', topupForm.amount);
      formData.append('transfer_type', topupForm.transfer_type);
      formData.append('reference_number', topupForm.reference_number);
      formData.append('payment_proof', topupForm.payment_proof);

      const response = await apiService.requestWalletTopup(formData);

      if (response.success) {
        toast.success('Top-up request submitted successfully!');
        setShowTopupModal(false);
        setTopupForm({
          amount: '',
          transfer_type: 'NEFT',
          reference_number: '',
          payment_proof: null
        });
        loadWalletData(); // Reload data
      } else {
        toast.error(response.error || 'Failed to submit top-up request');
      }
    } catch (error) {
      console.error('Top-up request failed:', error);
      toast.error(error.response?.data?.error || 'Failed to submit top-up request');
    } finally {
      setSubmittingTopup(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only JPG, PNG and PDF files are allowed');
        return;
      }

      setTopupForm({ ...topupForm, payment_proof: file });
    }
  };

  const refreshWallet = async () => {
    setRefreshing(true);
    await loadWalletData();
    setRefreshing(false);
    toast.success('Wallet refreshed');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getTransactionIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'credit':
      case 'payin':
      case 'settlement_credit':
      case 'manual_topup':
        return <TrendingUp className="h-5 w-5 text-green-600" />;
      case 'debit':
      case 'payout':
      case 'settlement_debit':
        return <TrendingDown className="h-5 w-5 text-red-600" />;
      default:
        return <WalletIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const getTransactionColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'credit':
      case 'payin':
      case 'settlement_credit':
      case 'manual_topup':
        return 'text-green-600';
      case 'debit':
      case 'payout':
      case 'settlement_debit':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getTransactionTypeLabel = (type) => {
    const labels = {
      'settlement_credit': 'Settlement Credit',
      'settlement_debit': 'Settlement Debit',
      'manual_topup': 'Manual Top-up',
      'payin': 'Pay-in',
      'payout': 'Payout',
      'credit': 'Credit',
      'debit': 'Debit',
      'topup': 'Top-up',
      'refund': 'Refund'
    };
    return labels[type?.toLowerCase()] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Wallet</h1>
        <div className="flex gap-3">
          <button
            onClick={() => setShowTopupModal(true)}
            className="btn-primary inline-flex items-center"
          >
            <Plus size={16} className="mr-2" />
            Wallet Top-up
          </button>
          <button
            onClick={refreshWallet}
            disabled={refreshing}
            className="btn-secondary inline-flex items-center"
          >
            <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Wallet Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Available Balance</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(wallet?.available_balance || 0)}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <WalletIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-xs text-gray-500">
              Available for payouts and withdrawals
            </p>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Processing Balance</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(wallet?.processing_balance || 0)}
              </p>
            </div>
            <div className="p-3 bg-orange-100 rounded-full">
              <Loader className="h-6 w-6 text-orange-600" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-xs text-gray-500">
              On-hold for pending/processing payouts
            </p>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Balance</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(wallet?.pending_balance || 0)}
              </p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-full">
              <TrendingUp className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-xs text-gray-500">
              Payin settlements waiting to credit
            </p>
          </div>
        </div>
      </div>

      {/* Wallet Information */}
      {wallet && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Wallet Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500">Currency</label>
              <p className="text-sm text-gray-900">{wallet.currency || 'INR'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Last Updated</label>
              <p className="text-sm text-gray-900">
                {wallet.updated_at ? formatDateTime(wallet.updated_at) : 'N/A'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Settlement Frequency</label>
              <p className="text-sm text-gray-900">{wallet.settlement_frequency || 'Daily'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Next Settlement</label>
              <p className="text-sm text-gray-900">
                {wallet.next_settlement_date
                  ? formatDate(wallet.next_settlement_date)
                  : 'Tomorrow'
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tabbed Content - Wallet Transactions & Top-up Requests */}
      <div className="card p-0">
        {/* Tab Headers */}
        <div className="px-6 pt-6 border-b border-gray-200">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab('transactions')}
              className={`pb-4 text-sm font-semibold uppercase tracking-wide transition-all relative ${
                activeTab === 'transactions'
                  ? 'text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Wallet Transactions
              {transactions.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-700 font-medium">
                  {transactions.length}
                </span>
              )}
              {activeTab === 'transactions' && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('topup')}
              className={`pb-4 text-sm font-semibold uppercase tracking-wide transition-all relative ${
                activeTab === 'topup'
                  ? 'text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Top-up Requests
              {topupRequests.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-700 font-medium">
                  {topupRequests.length}
                </span>
              )}
              {activeTab === 'topup' && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></span>
              )}
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="overflow-x-auto">
          {activeTab === 'transactions' ? (
            <>
              {/* Wallet Transactions Tab */}
              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reference
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Balance After
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.length > 0 ? (
                  transactions.map((transaction, index) => (
                    <tr key={transaction.id || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getTransactionIcon(transaction.reference_type || transaction.transaction_type)}
                          <div className="ml-2">
                            <div className="text-sm font-medium text-gray-900">
                              {getTransactionTypeLabel(transaction.reference_type || transaction.transaction_type)}
                            </div>
                            <div className={`text-xs ${
                              transaction.transaction_type?.toLowerCase() === 'credit'
                                ? 'text-green-600'
                                : 'text-red-600'
                            }`}>
                              {transaction.transaction_type?.toUpperCase()}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 max-w-xs">
                        <div className="text-sm text-gray-900 line-clamp-2">
                          {transaction.description || 'Wallet transaction'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {transaction.reference_id || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-semibold ${getTransactionColor(transaction.transaction_type)}`}>
                          {transaction.transaction_type?.toLowerCase() === 'debit' ? '-' : '+'}
                          {formatCurrency(Math.abs(transaction.amount || 0))}
                        </div>
                        {transaction.balance_before && (
                          <div className="text-xs text-gray-500">
                            Before: {formatCurrency(transaction.balance_before)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(transaction.balance_after || transaction.balance || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {transaction.created_at
                            ? formatDate(transaction.created_at)
                            : 'N/A'
                          }
                        </div>
                        <div className="text-xs text-gray-500">
                          {transaction.created_at
                            ? formatTime(transaction.created_at)
                            : ''
                          }
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                      <WalletIcon className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                      <p>No transactions found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {/* Transactions Pagination */}
            {transactionTotal > 10 && (
              <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Showing {Math.min((transactionPage - 1) * 10 + 1, transactionTotal)} to{' '}
                  {Math.min(transactionPage * 10, transactionTotal)} of {transactionTotal} transactions
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => setTransactionPage(prev => Math.max(1, prev - 1))}
                    disabled={transactionPage === 1}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-50 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setTransactionPage(prev => prev + 1)}
                    disabled={transactionPage * 10 >= transactionTotal}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-50 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
            </>
          ) : (
            <>
              {/* Top-up Requests Tab */}
              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Request ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transfer Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reference
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {topupRequests.length > 0 ? (
                  topupRequests.map((request) => (
                    <tr key={request.request_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {request.request_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {formatCurrency(request.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800">
                          {request.transfer_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {request.reference_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          request.status === 'approved'
                            ? 'bg-green-100 text-green-800'
                            : request.status === 'rejected'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {request.status?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(request.created_at)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatTime(request.created_at)}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                      <Upload className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                      <p>No top-up requests found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {/* Top-up Requests Pagination */}
            {topupTotal > 10 && (
              <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Showing {Math.min((topupPage - 1) * 10 + 1, topupTotal)} to{' '}
                  {Math.min(topupPage * 10, topupTotal)} of {topupTotal} requests
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => setTopupPage(prev => Math.max(1, prev - 1))}
                    disabled={topupPage === 1}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-50 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setTopupPage(prev => prev + 1)}
                    disabled={topupPage * 10 >= topupTotal}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-50 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
            </>
          )}
        </div>
      </div>

      {/* Top-up Modal */}
      {showTopupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Request Wallet Top-up</h2>
              <button
                onClick={() => setShowTopupModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleTopupSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="1"
                  value={topupForm.amount}
                  onChange={(e) => setTopupForm({ ...topupForm, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter amount"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transfer Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={topupForm.transfer_type}
                  onChange={(e) => setTopupForm({ ...topupForm, transfer_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                >
                  <option value="NEFT">NEFT</option>
                  <option value="RTGS">RTGS</option>
                  <option value="IMPS">IMPS</option>
                  <option value="UPI">UPI</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reference / UTR Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={topupForm.reference_number}
                  onChange={(e) => setTopupForm({ ...topupForm, reference_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter reference or UTR number"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Proof <span className="text-red-500">*</span>
                </label>
                <div className="mt-1 flex items-center">
                  <label className="w-full cursor-pointer">
                    <div className="flex items-center justify-center w-full px-4 py-2 border-2 border-gray-300 border-dashed rounded-md hover:border-primary-400">
                      <div className="space-y-1 text-center">
                        <Upload className="mx-auto h-8 w-8 text-gray-400" />
                        <div className="text-sm text-gray-600">
                          {topupForm.payment_proof ? (
                            <span className="text-primary-600 font-medium">
                              {topupForm.payment_proof.name}
                            </span>
                          ) : (
                            <span>Click to upload (JPG, PNG, PDF - Max 10MB)</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <input
                      type="file"
                      onChange={handleFileChange}
                      accept="image/jpeg,image/jpg,image/png,application/pdf"
                      className="hidden"
                      required
                    />
                  </label>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowTopupModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  disabled={submittingTopup}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 btn-primary"
                  disabled={submittingTopup}
                >
                  {submittingTopup ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wallet;